import { IonIcon } from '@ionic/react';
import {
  lockClosedOutline,
  layersOutline,
  musicalNoteOutline,
  playOutline,
  repeatOutline,
  timeOutline,
} from 'ionicons/icons';
import React from 'react';
import { splitPath } from '../duplicateFiles';
import { formatSeconds, TrackFacts } from '../duplicateVerdict';

/** Fact keys that differ between the copies in a set, so we can emphasise them. */
export type VaryingFacts = Set<
  'format' | 'length' | 'cues' | 'loops' | 'plays' | 'drm' | 'crates'
>;

type Props = {
  facts: TrackFacts;
  /** Shared across the set so the copies behave as one radio group. */
  setName: string;
  isKeeper: boolean;
  isRecommended: boolean;
  recommendation?: string;
  varying: VaryingFacts;
  index: number;
  onChoose: () => void;
};

const Chip: React.FC<{
  icon?: string;
  label: string;
  emphasised: boolean;
  tone?: 'work' | 'warn' | 'crate';
}> = ({ icon, label, emphasised, tone }) => (
  <span
    className={[
      'dup-fact',
      emphasised ? 'is-differing' : '',
      tone ? `dup-fact--${tone}` : '',
    ]
      .filter(Boolean)
      .join(' ')}
  >
    {icon && <IonIcon icon={icon} />}
    {label}
  </span>
);

/**
 * One copy in a duplicate set. Choosing a row means "keep this one" — the
 * others are what gets removed, which is stated on the row itself rather than
 * left for the user to infer.
 */
const CandidateRow: React.FC<Props> = ({
  facts,
  setName,
  isKeeper,
  isRecommended,
  recommendation,
  varying,
  index,
  onChoose,
}) => {
  const { dir, file } = splitPath(facts.track.filePath);
  const length = formatSeconds(facts.duration);
  const format = [
    facts.extension.toUpperCase(),
    facts.bitrate ? `${facts.bitrate} kbps` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <label
      className={`dup-candidate ${isKeeper ? 'is-keeping' : 'is-removing'}`}
    >
      <input
        type="radio"
        name={setName}
        checked={isKeeper}
        onChange={onChoose}
      />

      <span className="dup-candidate-mark" aria-hidden="true">
        <span className="dup-candidate-key">{index + 1}</span>
      </span>

      <span className="dup-candidate-body">
        <span className="dup-candidate-verdict">
          {isKeeper ? 'Keeping this copy' : 'Will be removed'}
        </span>

        <span className="dup-candidate-file" title={facts.track.filePath}>
          {file}
        </span>
        {dir && <span className="dup-candidate-dir">{dir}</span>}

        <span className="dup-candidate-facts">
          {format && (
            <Chip
              icon={musicalNoteOutline}
              label={format}
              emphasised={varying.has('format')}
            />
          )}
          {length && (
            <Chip
              icon={timeOutline}
              label={length}
              emphasised={varying.has('length')}
            />
          )}
          {facts.cues > 0 && (
            <Chip
              label={`${facts.cues} cue${facts.cues === 1 ? '' : 's'}`}
              emphasised={varying.has('cues')}
              tone="work"
            />
          )}
          {facts.loops > 0 && (
            <Chip
              icon={repeatOutline}
              label={`${facts.loops} loop${facts.loops === 1 ? '' : 's'}`}
              emphasised={varying.has('loops')}
              tone="work"
            />
          )}
          {facts.playCount > 0 && (
            <Chip
              icon={playOutline}
              label={`${facts.playCount} play${facts.playCount === 1 ? '' : 's'}`}
              emphasised={varying.has('plays')}
              tone="work"
            />
          )}
          {facts.crateCount > 0 && (
            <Chip
              icon={layersOutline}
              label={`${facts.crateCount} crate${facts.crateCount === 1 ? '' : 's'}`}
              emphasised={varying.has('crates')}
              tone="crate"
            />
          )}
          {facts.isDrm && (
            <Chip
              icon={lockClosedOutline}
              label="DRM protected"
              emphasised={varying.has('drm')}
              tone="warn"
            />
          )}
        </span>

        {isRecommended && recommendation && (
          <span className="dup-candidate-why">
            <strong>My pick:</strong> {recommendation}
          </span>
        )}
      </span>
    </label>
  );
};

export default CandidateRow;
